import React, { useState, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Box } from "@mui/material";

// --- Material Dashboard Pro React Components ---
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

// --- Styles from loginPageStyle.js for consistent look ---
import styles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";

const useStyles = makeStyles(styles);

export default function AffiliatePolicy() {
  const [cardAnimation, setCardAnimation] = useState("cardHidden");
  const isMountedRef = useRef(true);
  const classes = useStyles();

  // --- Card Animation Effect ---
  useEffect(() => {
    isMountedRef.current = true;
    const id = setTimeout(() => {
      if (isMountedRef.current) setCardAnimation("");
    }, 300);

    return () => {
      isMountedRef.current = false;
      clearTimeout(id);
    };
  }, []);

  return (
    <div className={classes.container}>
      <GridContainer justify="center">
        <GridItem xs={12} sm={10} md={8}>
          <Card login className={classes[cardAnimation]}>
            <CardHeader
              className={`${classes.cardHeader} ${classes.textCenter}`}
              color="rose"
            >
              <h4 className={classes.cardTitle}>Affiliate Policy</h4>
            </CardHeader>
            <CardBody>
              {/* --- Affiliate Policy Content Starts Here --- */}
              <Box sx={{ m: 0, p: 0 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Teamwork, guided by rules and regulations, ultimately leads to
                  success.
                </Typography>

                <Typography variant="body2" gutterBottom fontWeight="bold">
                  Last Update: 1st August 2025
                </Typography>

                <Box component="dl" mt={4}>
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (1). Accessing Beramour Application and Embedded Dating
                      Services.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Welcome to Beramour ("Beramour", "we", "our", or "us"). The
                    Beramour Dating Platform is a product of Beta Infinity Ltd.,
                    hereafter referred to as "the company". By accessing our
                    services—whether through a mobile device, tablet, computer,
                    or any other communication medium capable of presenting our
                    dating application—you agree to be bound by the following:
                    <ol type="a">
                      <li>Terms of Use,</li>
                      <li>Privacy Policy, and</li>
                      <li>Affiliate Policy.</li>
                    </ol>
                  </Typography>

                  <Typography component="dd" paragraph>
                    This Affiliate Policy outlines the terms and conditions for
                    participating in the affiliate program of Beramour, provided
                    "as is" by Beta Infinity Ltd. (hereinafter referred to as
                    "Company"), a company offering online dating services and
                    registered in Kenya. By registering and participating in the
                    affiliate program, affiliates acknowledge that they have
                    read, understood, and agreed to be bound by the terms and
                    conditions outlined in Beramour’s Affiliate Policy. By
                    registering an affiliate account or logging into our dating
                    platform, you consent to the collection and use of your
                    personal data in accordance with our Privacy Policy. We may
                    amend these Terms of Use, Privacy Policy, and Affiliate
                    Policy from time to time without prior notice. The date of
                    the last amendment is shown at the beginning of each
                    relevant document. You further agree that you are solely
                    responsible for regularly reviewing the current versions of
                    these three subject matter agreements. If you do not agree
                    with our Affiliate Policy, kindly cease using our affiliate
                    arrangement immediately.
                  </Typography>

                  {/* --- NEW: Definitions (clarifies referrals, conversions, fraud) --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(2). Definitions.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    For purposes of this Affiliate Policy:
                    <ol type="a">
                      <li>
                        <strong>"Affiliate"</strong> means a registered
                        affiliate partner with an active affiliate account on
                        Beramour.
                      </li>
                      <li>
                        <strong>"Referral Link/Code"</strong> means the unique
                        tracking link or code generated from an affiliate
                        account.
                      </li>
                      <li>
                        <strong>"Referred Client"</strong> means a user who
                        signs up using the Referral Link/Code and is attributed
                        to the affiliate under our tracking rules.
                      </li>
                      <li>
                        <strong>"Qualified Payment"</strong> means a successful
                        payment by a Referred Client that is not reversed,
                        refunded, charged back, or identified as fraudulent.
                      </li>
                    </ol>
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(3). Eligibility and Registration.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    (3.1) Eligibility: To be eligible for the affiliate program,
                    applicants must be at least 18 years old, have a valid email
                    address, and a payment receival account. The payment
                    receival account MUST be either a local mobile money wallet
                    for Kenyan residents or a PayPal account for affiliates
                    residing outside Kenya who do not have local mobile money
                    wallets, such as M-Pesa or Airtel Money.
                    <br />
                    (3.2) Registration: Affiliates must complete the
                    registration process on the Company’s platform. Since the
                    process is automated, affiliates will receive a unique
                    affiliate account that functions as a ledger, a payment
                    initiation platform, and a lead tracking system
                    simultaneously.
                    <br />
                    (3.3) Contractual Agreement: A contractual agreement between
                    the affiliate and the Company is automatically entered into
                    once the affiliate account is created.
                    <br />
                  </Typography>

                  {/* --- NEW: Compliance with Support Center / Brand Rules --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (4). Compliance, Promotional Standards, and Brand Safety.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Affiliates must comply with applicable laws, regulations,
                    and platform guidelines, and must not use misleading,
                    deceptive, or unfair marketing practices. Affiliates must
                    also follow Beramour’s brand, safety, and community rules as
                    published within the platform and/or Support Center where
                    applicable. The Company may require modification or removal
                    of promotional content that violates this policy or creates
                    user harm, regulatory exposure, or reputational risk.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(5). Affiliate Responsibilities.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    (5.1) Promotion: Affiliates are responsible for promoting
                    the Company’s dating site through various channels such as
                    websites, blogs, social media, and other marketing
                    platforms. This can also include their social circles or any
                    other methods they prefer.
                    <br />
                    (5.2) Generated Links: Affiliates will generate unique
                    affiliate referral codes from their accounts and share these
                    codes with their audience to drive traffic to the Company’s
                    platform.
                    <br />
                    (5.3) Compliance: Affiliates must comply with all applicable
                    laws, regulations, and guidelines. Misleading or deceptive
                    practices are strictly prohibited.
                    <br />
                  </Typography>

                  {/* --- NEW: Prohibited Affiliate Conduct (fraud, spam, impersonation) --- */}
                  <Typography component="dd" paragraph>
                    <strong>(5.4) Prohibited Conduct:</strong> Affiliates must
                    not:
                    <ol type="a">
                      <li>
                        engage in spam, unsolicited messaging, robocalls, or
                        bulk DM campaigns that violate platform policies or
                        laws;
                      </li>
                      <li>
                        misrepresent Beramour, Beta Infinity Ltd., pricing,
                        refunds, trial periods, safety features, or user
                        outcomes;
                      </li>
                      <li>
                        use fake accounts, bots, automated clicks, incentives,
                        or fraudulent methods to generate referrals;
                      </li>
                      <li>
                        impersonate Beramour staff, affiliates, or other
                        persons;
                      </li>
                      <li>
                        place Referral Links/Codes on illegal, hateful,
                        pornographic, violent, exploitative, or otherwise
                        harmful content;
                      </li>
                      <li>
                        bid on or use the Company’s trademarks in a way that
                        causes confusion as to official endorsement, unless
                        expressly authorized in writing by the Company.
                      </li>
                    </ol>
                    Breach of these rules may lead to withholding of
                    commissions, suspension, or termination.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (6). REVIEW B4 LAUNCH: Earnings and Commissions.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    (6.1) Commission Rate: Affiliates earn a commission of 10%
                    of the payments made by each client referred through their
                    affiliate links. There are no other commissions or earnings
                    outside this particular revenue stream.
                    <br />
                    (6.2) Taxable Income: Commissions earned within Kenya are
                    subject to either a 5% Withholding Tax or Pay As You Earn
                    (PAYE), as structured by law. The relevant taxable amount
                    will be deducted and remitted to the government as required
                    by Kenyan law.
                    <br />
                    (6.3) Earnings Accrual: Commissions accrue in the
                    affiliate’s account as long as the referred clients continue
                    to make payments. An affiliate will continue earning every
                    time the onboarded client pays.
                    <br />
                  </Typography>

                  {/* --- NEW: Attribution Rules + Disputes --- */}
                  <Typography component="dd" paragraph>
                    <strong>
                      (6.4) Attribution and Eligibility of Commissions:
                    </strong>{" "}
                    Commissions are earned only on Qualified Payments that are
                    properly attributed to the affiliate under our systems. The
                    Company reserves the right to determine attribution rules,
                    including how long a referral cookie or code remains valid,
                    how multiple referrals are handled, and how fraud or abuse
                    is addressed. Affiliates may request a review of an
                    attribution decision by contacting us using the details
                    below.
                    <br />
                  </Typography>

                  {/* --- NEW: Chargebacks / Refunds Impact on Commission --- */}
                  <Typography component="dd" paragraph>
                    <strong>(6.5) Chargebacks, Refunds, and Reversals:</strong>{" "}
                    If a Referred Client’s payment is refunded, reversed, or
                    charged back, the related commission may be reversed or
                    deducted from the affiliate’s balance. Where fraud is
                    suspected, the Company may hold or withhold commissions
                    pending investigation.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(7). REVIEW B4 LAUNCH: Payment Process.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    (7.1) Initiating Payments: Affiliates must initiate payment
                    requests through their own affiliate accounts on Beramour's
                    dating platform to receive their earnings.
                    <br />
                    (7.2) Payment Methods: Earnings shall be credited to the
                    affiliate's mobile wallet or PayPal account, as specified by
                    the affiliate during the account creation process.
                    <br />
                    (7.3) Payment Frequency: It is the responsibility of the
                    affiliate member whose account has a positive balance to
                    initiate the payment procedure through their account.
                    Payments are planned to be processed WITHIN THE FIRST 5
                    WORKING DAYS of the new month. To facilitate administrative
                    processing on the company's side, affiliates are advised to
                    initiate payment requests LATEST BY THE LAST DAY OF THE
                    MONTH BEING BILLED. Any requests made after this date will
                    be considered as payments for the current month. Payment
                    requests can only be made if there is a positive balance
                    sufficient to cover transaction costs, as charged by the
                    paying financial institutions.
                    <br />
                    (7.4) Delayed Payments: We recognize and acknowledge that
                    our affiliate partners play a key role in market development
                    and company growth. We intend to support the affiliate
                    program so that it runs smoothly and that affiliate members
                    receive their well-earned money in a timely manner. In the
                    rare occasion that an affiliate member does not receive the
                    claimed amounts BY THE END OF THE 7TH WORKING DAY of the new
                    month, affiliates are requested to immediately contact Beta
                    Infinity Ltd using the contact details below. This matter
                    will be treated by the company as a priority. We value our
                    teamwork.
                    <br />
                  </Typography>

                  {/* --- NEW: Verification / KYC-like checks (light, optional) --- */}
                  <Typography component="dd" paragraph>
                    <strong>(7.5) Verification and Compliance Checks:</strong>{" "}
                    To prevent fraud and comply with legal obligations, the
                    Company may request reasonable verification of affiliate
                    identity, payment details, and tax information before
                    processing payouts. Failure to provide requested information
                    may result in delayed or withheld payments, subject to
                    applicable law.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(8). Termination of Working Relationship.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    (8.1) Termination by Affiliate Partner: Affiliates may
                    terminate their participation in the program at any time by
                    deleting their accounts. It is recommended that affiliates
                    claim their positive account balances BEFORE DELETING THEIR
                    ACCOUNTS. Any unclaimed funds after account deletion will be
                    treated as extraordinary earnings by the company.
                    <br />
                    (8.2) Termination by the Company: The Company reserves the
                    right to terminate an affiliate's participation for any
                    reason, including but not limited to violation of this
                    policy, fraudulent activity, or non-compliance with
                    applicable laws. If the matter at hand arises from a
                    misunderstanding and can be resolved amicably through
                    dialogue, the decision to terminate the contractual
                    relationship may be reviewed. If the Company does terminate
                    an affiliate's account, the account holder will receive any
                    positive balances in line with the Company’s payment
                    procedures, subject to any lawful holds relating to fraud,
                    chargebacks, or compliance investigations.
                    <br />
                    (8.3) Effect of Termination: The deletion of the affiliate's
                    account immediately terminates the Company's contractual
                    relationship with the affiliate.
                    <br />
                  </Typography>

                  {/* --- NEW: Withholding / Forfeiture on Fraud --- */}
                  <Typography component="dd" paragraph>
                    <strong>(8.4) Fraud and Forfeiture:</strong> If the Company
                    reasonably determines that an affiliate has engaged in
                    fraud, abuse, or prohibited conduct, the Company may
                    withhold, reverse, or forfeit commissions and terminate the
                    affiliate relationship immediately, subject to applicable
                    law.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(9). Confidentiality and Data Protection.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    (9.1) Confidential Information: Affiliates agree to keep all
                    non-public information about the Company confidential. At
                    the same time, they are expected to hold data pertaining to
                    clients onboarded by them through the affiliate program
                    strictly confidential.
                    <br />
                    (9.2) Data Protection: The Company will handle personal data
                    in accordance with applicable data protection laws and the
                    Company’s Privacy Policy.
                    <br />
                  </Typography>

                  {/* --- NEW: No Ownership / Relationship Clarification --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(10). Independent Contractor Relationship.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Affiliates participate as independent contractors. Nothing
                    in this Affiliate Policy creates an employment relationship,
                    agency, partnership, or joint venture between the affiliate
                    and the Company. Affiliates may not bind the Company or make
                    representations on behalf of the Company without prior
                    written authorization.
                    <br />
                  </Typography>

                  {/* --- NEW: Limitation of Liability + Indemnity (affiliate context) --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (11). Limitation of Liability and Indemnity.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    To the fullest extent permitted by law, the Company shall
                    not be liable for indirect, incidental, consequential, or
                    punitive damages arising from the affiliate program,
                    including loss of profits or business opportunities. You
                    agree to indemnify and hold harmless the Company from
                    claims, losses, and expenses (including reasonable legal
                    fees) arising from your promotional activities, violation of
                    this policy, or breach of applicable law.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(12). Amendments to This Affiliate Policy.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    We may update this Affiliate Policy from time to time to
                    reflect changes in our practices or legal requirements. We
                    will notify you of any material changes by posting the
                    updated content on this page. The date of the latest update
                    is indicated at the top. Your continued use of our services
                    after such changes constitutes your acceptance of the
                    revised Affiliate Policy.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(13). Governing Law.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    This Affiliate Policy is governed by and construed in
                    accordance with the Laws of the Republic of Kenya. Any
                    disputes arising out of or in connection with this policy
                    shall be subject to the exclusive jurisdiction of the courts
                    of Kenya. Court cases can only be filed in any court within
                    the County of Nairobi, Kenya.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(14). Contact Details.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Beramour Dating Platform
                    <br />
                    Beta Infinity Ltd
                    <br />
                    PO Box 7129,
                    <br />
                    00200 City Square,
                    <br />
                    Nairobi, Kenya
                    <br />
                    Contact: support.beramour.com (Open a Ticket)
                    <br />
                    Beramour Dating Platform is a product of Beta Infinity Ltd.
                  </Typography>
                </Box>
              </Box>
              {/* --- Affiliate Policy Content Ends Here --- */}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
